// src/index.ts
import isEqual from "fast-deep-equal";
import { HYDRATE } from "next-redux-wrapper";

// src/config.ts
function processMiddlewareConfig(config) {
  const { subtrees, ...globalSubtreeConfig } = {
    ignoreStateFromStaticProps: true,
    path: "/",
    sameSite: true,
    ...config
  };
  return subtrees.map((current) => {
    if (typeof current === "string") {
      current = { subtree: current };
    }
    const {
      subtree,
      ignoreStateFromStaticProps,
      cookieName,
      defaultState,
      compress,
      serializationFunction,
      deserializationFunction,
      ...cookieOptions
    } = {
      ...globalSubtreeConfig,
      cookieName: current.subtree,
      ...current
    };
    return {
      subtree,
      ignoreStateFromStaticProps,
      cookieName,
      defaultState,
      compress: compress != null ? compress : !(serializationFunction != null ? serializationFunction : deserializationFunction),
      serializationFunction,
      deserializationFunction,
      cookieOptions
    };
  });
}

// src/cookies.ts
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from "lz-string";
import { destroyCookie, parseCookies, setCookie } from "nookies";

// src/util.ts
var isClient = () => typeof window !== "undefined";
var isSsrContext = (context) => context.req !== void 0 && context.res !== void 0;
var isAppContext = (context) => context.ctx !== void 0;

// src/cookies.ts
var StateCookies = class {
  static _encodeState(state, { compress, serializationFunction }) {
    const serializedState = (serializationFunction != null ? serializationFunction : JSON.stringify)(state);
    return (compress ? compressToEncodedURIComponent : encodeURIComponent)(serializedState);
  }
  static _decodeState(state, { compress, deserializationFunction }) {
    const decodedState = (compress ? decompressFromEncodedURIComponent : decodeURIComponent)(
      state
    );
    return (deserializationFunction != null ? deserializationFunction : JSON.parse)(decodedState);
  }
  _config = /* @__PURE__ */ new Map();
  _context;
  _cookies;
  constructor(context) {
    this._context = context;
  }
  setConfigurations(configurations) {
    for (const config of configurations) {
      this._config.set(config.cookieName, config);
    }
  }
  getAll() {
    if (typeof this._cookies === "undefined" || isClient()) {
      this._cookies = {};
      const allCookies = parseCookies(this._context, { decode: String });
      for (const [cookieName, cookieConfig] of this._config.entries()) {
        if (typeof allCookies[cookieName] !== "undefined") {
          this._cookies[cookieName] = StateCookies._decodeState(
            allCookies[cookieName],
            cookieConfig
          );
        } else if (typeof cookieConfig.defaultState !== "undefined") {
          this._cookies[cookieName] = cookieConfig.defaultState;
        }
      }
    }
    return this._cookies;
  }
  set(name, state) {
    const cookieConfig = this._config.get(name);
    const encodedState = StateCookies._encodeState(state, cookieConfig);
    setCookie(this._context, name, encodedState, {
      ...cookieConfig.cookieOptions,
      encode: String,
      httpOnly: false
    });
  }
  delete(name) {
    const { cookieOptions } = this._config.get(name);
    destroyCookie(this._context, name, {
      ...cookieOptions,
      httpOnly: false
    });
  }
};

// src/state-walker.ts
import { produce } from "immer";
import get from "lodash/get";
import set from "lodash/set";
var walkState = (subtrees, walker, stateA, stateB) => produce(stateA, (draftState) => {
  for (const subtreeConfig of subtrees) {
    const { subtree } = subtreeConfig;
    const result = walker(subtreeConfig, get(stateA, subtree), get(stateB, subtree));
    if (typeof result !== "undefined") {
      set(draftState, subtree, result);
    }
  }
});

// src/index.ts
var SERVE_COOKIES = "next-redux-cookie-wrapper/SERVE_COOKIES";
var wrapMakeStore = (makeStore) => (context) => {
  const store = makeStore(context);
  if (isClient()) {
    store.dispatch({ type: HYDRATE, payload: {} });
  } else {
    if (isAppContext(context)) {
      context = context.ctx;
    }
    if (isSsrContext(context)) {
      store.dispatch({
        type: SERVE_COOKIES,
        payload: new StateCookies(context)
      });
    }
  }
  return store;
};
var nextReduxCookieMiddleware = (config) => (store) => {
  const subtrees = processMiddlewareConfig(config);
  let cookies;
  if (isClient()) {
    cookies = new StateCookies();
    cookies.setConfigurations(subtrees);
  }
  return (next) => (action) => {
    switch (action.type) {
      case SERVE_COOKIES: {
        cookies = action.payload;
        cookies.setConfigurations(subtrees);
        const allCookies = cookies.getAll();
        store.dispatch({
          type: HYDRATE,
          payload: walkState(subtrees, (subtree) => allCookies[subtree.cookieName], {})
        });
        return;
      }
      case HYDRATE:
        if (isClient()) {
          const allCookies = cookies.getAll();
          action.payload = walkState(
            subtrees,
            ({ ignoreStateFromStaticProps, cookieName }) => {
              if (ignoreStateFromStaticProps) {
                return allCookies[cookieName];
              }
            },
            action.payload
          );
        }
      default: {
        const oldState = store.getState();
        const result = next(action);
        const newState = store.getState();
        const isServerSideHydrateAction = !isClient() && action.type === HYDRATE;
        if (cookies) {
          walkState(
            subtrees,
            ({ cookieName, defaultState }, oldSubtreeState, newSubtreeState) => {
              const originState = isServerSideHydrateAction ? cookies.getAll()[cookieName] : oldSubtreeState;
              if (!isEqual(originState, newSubtreeState)) {
                if (typeof defaultState !== "undefined" && isEqual(newSubtreeState, defaultState)) {
                  cookies.delete(cookieName);
                } else {
                  cookies.set(cookieName, newSubtreeState);
                }
              }
            },
            oldState,
            newState
          );
        }
        return result;
      }
    }
  };
};
export {
  SERVE_COOKIES,
  nextReduxCookieMiddleware,
  wrapMakeStore
};
//# sourceMappingURL=index.mjs.map